import {
  Employee,
  Merchant,
  Store,
  StoreProduct,
  StoreProductFraction,
} from '@bofrak-backend/shared';
import { WarningTwoIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useSharedUiTranslation } from '../../i18n';

/**
 * Minimum |stored - expected| value that counts as drift worth surfacing.
 * Kept in sync with backend `REPAIR_DRIFT_THRESHOLD` in
 * `repair-parent-fractions.use-case.ts` so FE detection and BE repair agree.
 */
const DRIFT_NOISE_THRESHOLD = 0.0005;

/** Δ above this value is highlighted as a "large" drift in the modal. */
const DRIFT_LARGE_THRESHOLD = 0.05;

// ── Cost drift constants (mirrors audit-child-cost-drift.ts) ──────────

/** Absolute cost difference threshold (in currency units). */
const COST_DRIFT_ABS_THRESHOLD = 1;

/** Relative cost difference threshold (0.1%). */
const COST_DRIFT_RATIO_THRESHOLD = 0.001;

// ── Types ──────────────────────────────────────────────────────────────

export interface ProductDriftRow {
  child_id: string;
  child_name: string;
  child_qty_in_unit_package: number;
  parent_qty_in_unit_package: number;
  stored_fraction: number;
  expected_fraction: number;
  delta: number;
}

/**
 * Compare each child's stored `fraction` to the expected ratio
 * `childQty / parentQty`. Returns one row per drifted child; empty when
 * the product isn't a parent, has no children, or every child is within
 * the noise threshold.
 */
export function detectProductDrift(
  product: StoreProduct | null | undefined,
): ProductDriftRow[] {
  if (!product || !product.is_parent) return [];
  const parentQty = Math.max(
    1,
    Math.round(product.quantity_in_unit_package ?? 1),
  );
  const rows: ProductDriftRow[] = [];
  for (const f of product.store_product_fractions ?? []) {
    if (f.id === product.id) continue;
    if (f.fraction >= 1) continue;
    const childQty = Math.max(1, Math.round(f.quantity_in_unit_package ?? 1));
    const expected = childQty / parentQty;
    const delta = Math.abs((f.fraction ?? 0) - expected);
    if (delta <= DRIFT_NOISE_THRESHOLD) continue;
    rows.push({
      child_id: f.id,
      child_name:
        (f as StoreProductFraction).fraction_name ||
        String((f as StoreProductFraction).unit ?? '') ||
        f.id,
      child_qty_in_unit_package: childQty,
      parent_qty_in_unit_package: parentQty,
      stored_fraction: f.fraction,
      expected_fraction: expected,
      delta,
    });
  }
  return rows;
}

export interface CostDriftRow {
  child_id: string;
  child_name: string;
  child_qty_in_unit_package: number;
  parent_qty_in_unit_package: number;
  child_cost: number;
  parent_cost: number;
  expected_child_cost: number;
  abs_diff: number;
}

/**
 * Compare each child's stored `cost` to the expected proportional cost
 * `parentCost × (childQty / parentQty)`. Returns one row per drifted
 * child; empty when the product isn't a parent, has no children, or
 * every child is within the noise thresholds.
 *
 * Mirrors the logic in `scripts/audit-child-cost-drift.ts`.
 */
export function detectCostDrift(
  product: StoreProduct | null | undefined,
): CostDriftRow[] {
  if (!product || !product.is_parent) return [];
  const parentQty = Math.max(
    1,
    Math.round(product.quantity_in_unit_package ?? 1),
  );
  const parentCost = product.cost ?? 0;
  if (parentCost <= 0) return [];

  const rows: CostDriftRow[] = [];
  for (const f of product.store_product_fractions ?? []) {
    if (f.id === product.id) continue;
    const childQty = Math.max(1, Math.round(f.quantity_in_unit_package ?? 1));
    const childCost = f.cost ?? 0;
    const expected = Math.round(parentCost * (childQty / parentQty));
    const absDiff = Math.abs(childCost - expected);

    const mismatched =
      absDiff > COST_DRIFT_ABS_THRESHOLD &&
      (expected === 0
        ? absDiff > 0
        : absDiff / Math.abs(expected) > COST_DRIFT_RATIO_THRESHOLD);

    if (!mismatched) continue;

    rows.push({
      child_id: f.id,
      child_name:
        (f as StoreProductFraction).fraction_name ||
        String((f as StoreProductFraction).unit ?? '') ||
        f.id,
      child_qty_in_unit_package: childQty,
      parent_qty_in_unit_package: parentQty,
      child_cost: childCost,
      parent_cost: parentCost,
      expected_child_cost: expected,
      abs_diff: absDiff,
    });
  }
  return rows;
}

// ── Formatting helpers ─────────────────────────────────────────────────

const formatFraction = (n: number) =>
  Number.isFinite(n) ? n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '') : '—';

const fmtMoney = (n: number): string =>
  Number.isFinite(n) ? n.toFixed(2) : '—';

export interface ProductDriftAlertProps {
  product: StoreProduct;
  merchant: Merchant;
  store: Store;
  updator: Pick<Employee, 'id' | 'name'>;
  /** Optional: invoked after a successful repair (e.g. to refresh page state) */
  onFixed?: (productId: string) => void;
  /**
   * Visual style of the trigger:
   * - `icon` (default): tiny warning icon button, good for tight card headers.
   * - `banner`: full-width red banner with explanation + button, good for
   *   action surfaces (counting, purchasing) where the user must address the
   *   problem before continuing.
   */
  variant?: 'icon' | 'banner';
}

/**
 * Tiny alert IconButton that surfaces fraction-drift on a parent product
 * and lets the merchant repair it self-service. Renders nothing when no
 * drift is detected, so callers can drop it into any header row safely.
 */
export const ProductDriftAlert: React.FC<ProductDriftAlertProps> = ({
  product,
  variant = 'icon',
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useSharedUiTranslation();

  const driftRows = useMemo(() => detectProductDrift(product), [product]);
  const costDriftRows = useMemo(() => detectCostDrift(product), [product]);

  const totalIssues = driftRows.length + costDriftRows.length;

  if (totalIssues === 0) return null;

  const parentQty =
    driftRows[0]?.parent_qty_in_unit_package ??
    costDriftRows[0]?.parent_qty_in_unit_package ??
    1;
  const parentCost = costDriftRows[0]?.parent_cost ?? product.cost ?? 0;

  const hasFractionDrift = driftRows.length > 0;
  const hasCostDrift = costDriftRows.length > 0;

  const trigger =
    variant === 'banner' ? (
      <Box
        borderWidth="1px"
        borderColor="red.300"
        bg="red.50"
        p={2}
        borderRadius="md"
        w="full">
        <HStack spacing={2} align="center">
          <WarningTwoIcon color="red.500" boxSize={4} flexShrink={0} />
          <Text fontSize="xs" color="red.800" flex={1}>
            {t('This product has')}{' '}
            <strong>{t('{{count}} issues', { count: totalIssues })}</strong>{' '}
            {t('that need attention. Stock and costs may be off.')}
          </Text>
          <Button
            size="xs"
            colorScheme="red"
            leftIcon={<WarningTwoIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}>
            {t('Review & fix')}
          </Button>
        </HStack>
      </Box>
    ) : (
      <Tooltip
        label={t('{{count}} issues found — tap to review and fix', {
          count: totalIssues,
        })}
        placement="top">
        <IconButton
          aria-label={t('This product has issues — tap to fix')}
          icon={<WarningTwoIcon />}
          size="xs"
          variant="ghost"
          colorScheme="red"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        />
      </Tooltip>
    );

  return (
    <>
      {trigger}

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: 'full', md: 'xl' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <WarningTwoIcon color="red.500" />
              <Text>
                {t('Something looks wrong with “{{name}}”', {
                  name: product.name,
                })}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.700">
                {t('Quantity in Package:')} <strong>{parentQty}</strong>{' '}
                {t('units')}
                {parentCost > 0 && (
                  <>
                    {' '}
                    | {t('Cost:')} <strong>{fmtMoney(parentCost)}</strong>
                  </>
                )}
              </Text>

              {/* ── Fraction drift ── */}
              {hasFractionDrift && (
                <>
                  <Text fontSize="sm" fontWeight="bold" color="red.600">
                    ⚠{' '}
                    {t('Fraction mismatch ({{count}} children)', {
                      count: driftRows.length,
                    })}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    {t(
                      'The stored fraction does not match the expected ratio of quantities.',
                    )}
                  </Text>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('Child')}</Th>
                          <Th isNumeric>{t('Qty In Pkg')}</Th>
                          <Th isNumeric>{t('Saved now')}</Th>
                          <Th isNumeric>{t('Should be')}</Th>
                          <Th isNumeric>{t('Difference')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {driftRows.map((r) => {
                          const isLarge = r.delta > DRIFT_LARGE_THRESHOLD;
                          return (
                            <Tr key={r.child_id}>
                              <Td>{r.child_name}</Td>
                              <Td isNumeric>{r.child_qty_in_unit_package}</Td>
                              <Td isNumeric>
                                {formatFraction(r.stored_fraction)}
                              </Td>
                              <Td isNumeric>
                                {formatFraction(r.expected_fraction)}
                              </Td>
                              <Td isNumeric>
                                <Badge
                                  colorScheme="red"
                                  variant={isLarge ? 'solid' : 'subtle'}
                                  fontSize="2xs">
                                  {r.delta.toFixed(4)}
                                </Badge>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}

              {/* ── Cost drift ── */}
              {hasCostDrift && (
                <>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600">
                    💰{' '}
                    {t('Cost mismatch ({{count}} children)', {
                      count: costDriftRows.length,
                    })}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    {t('The child cost does not equal')}{' '}
                    <code>parent.cost ÷ parent.qty × child.qty</code>.{' '}
                    {t(
                      'Edit the product and re-save the parent cost to recalculate all child costs automatically.',
                    )}
                  </Text>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('Child')}</Th>
                          <Th isNumeric>{t('Qty In Pkg')}</Th>
                          <Th isNumeric>{t('Stored cost')}</Th>
                          <Th isNumeric>{t('Expected cost')}</Th>
                          <Th isNumeric>Δ</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {costDriftRows.map((r) => {
                          const isLarge = r.abs_diff > 5;
                          return (
                            <Tr key={r.child_id}>
                              <Td>{r.child_name}</Td>
                              <Td isNumeric>{r.child_qty_in_unit_package}</Td>
                              <Td isNumeric>{fmtMoney(r.child_cost)}</Td>
                              <Td isNumeric>
                                {fmtMoney(r.expected_child_cost)}
                              </Td>
                              <Td isNumeric>
                                <Badge
                                  colorScheme="orange"
                                  variant={isLarge ? 'solid' : 'subtle'}
                                  fontSize="2xs">
                                  {fmtMoney(r.abs_diff)}
                                </Badge>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="xs" color="gray.500">
              {hasFractionDrift && hasCostDrift
                ? t(
                    'Edit the fractions first, then edit the product and re-save the parent cost.',
                  )
                : hasFractionDrift
                  ? t(
                      'Edit the product qty and re-save to recalculate child fractions.',
                    )
                  : t(
                      'Edit the product and re-save the parent cost to recalculate child costs.',
                    )}
            </Text>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
